import { BrushIcon, Tag, CheckCircle2 } from "lucide-react";
import { getCloudinaryImageUrl } from "../../../utils/getCloudinaryImageUrl";

const PackageReviewStep = ({ pkg }) => {
  const hasDiscount = pkg.discountedPrice && pkg.discountedPrice < pkg.price;
  const discountPct = hasDiscount
    ? Math.round(((pkg.price - pkg.discountedPrice) / pkg.price) * 100)
    : 0;

  return (
    <div className="flex flex-col">
      {/* BANNER IMAGE */}
      {pkg.banner ? (
        <div className="relative h-56 w-full flex-shrink-0 overflow-hidden">
          <img
            src={getCloudinaryImageUrl(pkg.banner, {
              width: 600,
              quality: "auto",
              format: "auto",
            })}
            alt={pkg.name}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover scale-105 transition-transform duration-500"
          />

          {/*  Gradient (improved depth) */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />

          {/* TOP ROW */}
          <div className="absolute top-3 left-3 right-3 flex justify-between items-start">
            {/* Category */}
            {pkg.category && (
              <span className="backdrop-blur-md bg-white/80 text-[#ab310c] text-[10px] font-semibold px-3 py-1 rounded-full uppercase tracking-wide shadow">
                {pkg.category}
              </span>
            )}

            {/* Discount */}
            {hasDiscount && (
              <span className="backdrop-blur-md bg-green-500/90 text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow">
                {discountPct}% OFF
              </span>
            )}
          </div>

          {/* TITLE */}
          <div className="absolute bottom-4 left-4 right-4">
            <h3 className="text-white font-bold text-xl uppercase tracking-wide leading-tight drop-shadow-lg">
              {pkg.name}
            </h3>
          </div>
        </div>
      ) : (
        <div className="h-32 bg-gradient-to-br from-[#ab310c]/10 to-[#EDB5A4]/20 flex items-center justify-center">
          <h3 className="text-[#ab310c] font-bold text-2xl uppercase tracking-wide">
            {pkg.name}
          </h3>
        </div>
      )}


      <div className="p-5 space-y-5">
        {/* PRICE */}
        <div className="flex items-center justify-between bg-gradient-to-r from-[#ab310c]/5 to-[#EDB5A4]/10 rounded-2xl px-4 py-3 border border-[#ab310c]/10">
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Package Price</p>
            <div className="flex items-end gap-2">
              {hasDiscount ? (
                <>
                  <span className="text-2xl font-bold text-[#ab310c]">
                    ₹{pkg.discountedPrice}
                  </span>
                  <span className="text-sm text-gray-400 line-through mb-0.5">
                    ₹{pkg.price}
                  </span>
                </>
              ) : (
                <span className="text-2xl font-bold text-[#ab310c]">
                  ₹{pkg.price}
                </span>
              )}
            </div>
          </div>

          {hasDiscount && (
            <div className="text-right">
              <p className="text-xs text-gray-500">You save</p>
              <p className="text-base font-bold text-green-600">
                ₹{pkg.price - pkg.discountedPrice}
              </p>
            </div>
          )}
        </div>

        {/* DESCRIPTION */}
        {pkg.description && (
          <p className="text-sm text-gray-600 leading-relaxed">{pkg.description}</p>
        )}

        {/* SERVICES */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <BrushIcon size={15} className="text-[#ab310c]" />
            <h4 className="text-sm font-semibold text-gray-800">
              Included Services ({pkg.services?.length || 0})
            </h4>
          </div>

          <div className="space-y-2">
            {pkg.services?.map((serviceName, i) => (
              <div
                key={i}
                className="flex items-center gap-3 py-2 px-3 rounded-xl bg-gray-50 border border-gray-100"
              >
                <div className="w-5 h-5 rounded-full bg-[#ab310c]/10 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 size={12} className="text-[#ab310c]" />
                </div>
                <span className="text-sm text-gray-700">{serviceName}</span>
              </div>
            ))}
          </div>
        </div>

        {/* PAY AFTER NOTE */}
        <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-xl p-3">
          <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Tag size={12} className="text-blue-600" />
          </div>
          <p className="text-xs text-blue-700 leading-relaxed">
            <span className="font-semibold">Pay after service</span> — You only pay after our professional completes the service at your doorstep.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PackageReviewStep;